import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  differenceInCalendarDays,
  format,
  isValid,
  parseISO,
} from 'date-fns';
import type { DataPoint } from '@/features/chartsView';
import { prepareDateRangeValueObject } from '@/shared/api';
import type { SerializedDateRangeValueObject } from '@/shared/types';
import { CHART_BASE_DIMENSION } from './constants';

const WEEK_PATTERN = /^(\d{4})-W(\d{1,2})(?=\s|$)/;
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}(?=[\sT]|$)/;
const TIMED_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}[\sT]\d{2}:\d{2}/;
const MONTH_PATTERN = /^\d{4}-\d{2}(?=\s|$)/;
const YEAR_PATTERN = /^(\d{4})(?=\s|$)/;
const QUARTER_PATTERN = /^Q([1-4])(?=\s|$)/i;
const BARE_MONTH_PATTERN = /^(\d{1,2})\D*?([A-Za-z]{3,})/;

const MONTH_NAMES = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

const ISO_DATE_LENGTH = 10;

export const resolveCohortAnchor = (
  range: SerializedDateRangeValueObject,
): Date | null => {
  const [, to] = prepareDateRangeValueObject(range);
  const end = parseISO(to);

  return isValid(end) ? end : null;
};

type CohortPeriodKind =
  | 'year'
  | 'quarter'
  | 'month'
  | 'week'
  | 'day'
  | 'dayRange';

interface CohortPeriod {
  kind: CohortPeriodKind;
  start: Date;
}

type CohortPeriodResolver = (
  label: string,
  anchor: Date | null,
) => CohortPeriod | null;

const resolveAnchoredYear = (startMonth: number, anchor: Date): number =>
  startMonth <= anchor.getMonth()
    ? anchor.getFullYear()
    : anchor.getFullYear() - 1;

const resolveWeekPeriod: CohortPeriodResolver = (label) => {
  const match = WEEK_PATTERN.exec(label);

  if (!match) {
    return null;
  }

  const [, year, weekNumber] = match;
  const start = parseISO(`${year}-W${weekNumber.padStart(2, '0')}`);

  return isValid(start) ? { kind: 'week', start } : null;
};

const resolveDayPeriod: CohortPeriodResolver = (label) => {
  const match = DAY_PATTERN.exec(label);

  if (!match) {
    return null;
  }

  const start = parseISO(match[0]);
  const kind = TIMED_DAY_PATTERN.test(label) ? 'day' : 'dayRange';

  return isValid(start) ? { kind, start } : null;
};

const resolveIsoMonthPeriod: CohortPeriodResolver = (label) => {
  const match = MONTH_PATTERN.exec(label);

  if (!match) {
    return null;
  }

  const start = parseISO(match[0]);

  return isValid(start) ? { kind: 'month', start } : null;
};

const resolveYearPeriod: CohortPeriodResolver = (label) => {
  const match = YEAR_PATTERN.exec(label);

  return match
    ? { kind: 'year', start: new Date(Number(match[1]), 0, 1) }
    : null;
};

const resolveQuarterPeriod: CohortPeriodResolver = (label, anchor) => {
  if (!anchor) {
    return null;
  }

  const match = QUARTER_PATTERN.exec(label);

  if (!match) {
    return null;
  }

  const startMonth = (Number(match[1]) - 1) * 3;

  return {
    kind: 'quarter',
    start: new Date(resolveAnchoredYear(startMonth, anchor), startMonth, 1),
  };
};

const resolveBareMonthPeriod: CohortPeriodResolver = (label, anchor) => {
  if (!anchor) {
    return null;
  }

  const match = BARE_MONTH_PATTERN.exec(label);

  if (!match) {
    return null;
  }

  const monthIndex = Number(match[1]) - 1;

  if (!MONTH_NAMES[monthIndex]?.startsWith(match[2].toLowerCase())) {
    return null;
  }

  return {
    kind: 'month',
    start: new Date(resolveAnchoredYear(monthIndex, anchor), monthIndex, 1),
  };
};

// Order matters: "2026-10-14" would read as October 2026 if the month resolvers ran first.
const PERIOD_RESOLVERS: CohortPeriodResolver[] = [
  resolveWeekPeriod,
  resolveDayPeriod,
  resolveIsoMonthPeriod,
  resolveYearPeriod,
  resolveQuarterPeriod,
  resolveBareMonthPeriod,
];

const PERIOD_END: Record<
  CohortPeriodKind,
  (start: Date, stepDays: number) => Date
> = {
  year: (start) => addYears(start, 1),
  quarter: (start) => addMonths(start, 3),
  month: (start) => addMonths(start, 1),
  week: (start) => addWeeks(start, 1),
  day: (start) => addDays(start, 1),
  dayRange: (start, stepDays) => addDays(start, stepDays),
};

const resolveCohortPeriod = (
  label: string,
  anchor: Date | null,
): CohortPeriod | null => {
  for (const resolveFrom of PERIOD_RESOLVERS) {
    const period = resolveFrom(label, anchor);

    if (period) {
      return period;
    }
  }

  return null;
};

const resolveStepDays = (periods: CohortPeriod[]): number => {
  const times = periods
    .filter((period) => period.kind === 'dayRange')
    .map((period) => period.start.getTime())
    .sort((a, b) => a - b);

  let step = 0;

  for (let index = 1; index < times.length; index += 1) {
    const gap = differenceInCalendarDays(times[index], times[index - 1]);

    if (gap > 0 && (step === 0 || gap < step)) {
      step = gap;
    }
  }

  return step || 1;
};

export const resolveCohortCutoffs = (
  labels: string[],
  anchor: Date | null = null,
): Map<string, string> => {
  const periods = new Map<string, CohortPeriod>();

  for (const label of labels) {
    const period = resolveCohortPeriod(label, anchor);

    if (period) {
      periods.set(label, period);
    }
  }

  const stepDays = resolveStepDays([...periods.values()]);
  const cutoffs = new Map<string, string>();

  for (const [label, period] of periods) {
    cutoffs.set(
      label,
      format(PERIOD_END[period.kind](period.start, stepDays), 'yyyy-MM-dd'),
    );
  }

  return cutoffs;
};

interface SplitCohortCutoffParams {
  enabled: boolean;
  splitDimension: string;
  isChronological: boolean;
  splitOptions: string[] | undefined;
  cohortRangeEnd: Date | null;
}

interface SelectedCohortCutoffParams extends SplitCohortCutoffParams {
  excludedSplitOptions: string[];
}

export const resolveSplitCohortCutoffs = ({
  enabled,
  splitDimension,
  isChronological,
  splitOptions,
  cohortRangeEnd,
}: SplitCohortCutoffParams): Map<string, string> => {
  if (!enabled) {
    return new Map();
  }

  if (splitDimension === CHART_BASE_DIMENSION || !isChronological) {
    return new Map();
  }

  return resolveCohortCutoffs(splitOptions ?? [], cohortRangeEnd);
};

export const resolveSelectedCohortCutoffs = ({
  excludedSplitOptions,
  ...params
}: SelectedCohortCutoffParams): Map<string, string> => {
  const cutoffs = resolveSplitCohortCutoffs(params);

  for (const option of excludedSplitOptions) {
    cutoffs.delete(option);
  }

  return cutoffs;
};

export const trimImmatureCohorts = <T extends string>(
  data: DataPoint<T>[],
  xKey: T,
  cutoffs: Map<string, string>,
): DataPoint<T>[] => {
  if (cutoffs.size === 0) {
    return data;
  }

  const trimmed: DataPoint<T>[] = [];

  for (const point of data) {
    const snapshot = point[xKey].slice(0, ISO_DATE_LENGTH);
    const mature: Record<string, Record<string, number>> = {};
    let hasMature = false;

    for (const [option, values] of Object.entries(point.data)) {
      const cutoff = cutoffs.get(option);

      if (cutoff === undefined || snapshot >= cutoff) {
        mature[option] = values;
        hasMature = true;
      }
    }

    if (hasMature) {
      trimmed.push({ ...point, data: mature });
    }
  }

  return trimmed;
};

export const resolveOpenCohorts = <T extends string>(
  data: DataPoint<T>[],
  xKey: T,
  cutoffs: Map<string, string>,
): string[] => {
  if (cutoffs.size === 0) {
    return [];
  }

  const closed = new Set<string>();

  for (const point of data) {
    const snapshot = point[xKey].slice(0, ISO_DATE_LENGTH);

    for (const option of Object.keys(point.data)) {
      const cutoff = cutoffs.get(option);

      if (cutoff !== undefined && snapshot >= cutoff) {
        closed.add(option);
      }
    }
  }

  return [...cutoffs.keys()].filter((option) => !closed.has(option));
};

const hasVisibleValues = <T extends string>(
  point: DataPoint<T>,
  excluded: Set<string>,
): boolean => Object.keys(point.data).some((option) => !excluded.has(option));

export const hasVisibleCohortData = <T extends string>(
  data: DataPoint<T>[],
  excludedSplitOptions: string[],
): boolean => {
  const excluded = new Set(excludedSplitOptions);

  return data.some((point) => hasVisibleValues(point, excluded));
};

export const narrowToVisibleSnapshots = <T extends string>(
  data: DataPoint<T>[],
  excludedSplitOptions: string[],
): DataPoint<T>[] => {
  const excluded = new Set(excludedSplitOptions);

  let first = 0;

  while (first < data.length && !hasVisibleValues(data[first], excluded)) {
    first += 1;
  }

  // Emptying here would read as `noData`, which wins over `noSelection` in the wrapper.
  if (first === data.length) {
    return data;
  }

  let last = data.length - 1;

  while (last > first && !hasVisibleValues(data[last], excluded)) {
    last -= 1;
  }

  if (first === 0 && last === data.length - 1) {
    return data;
  }

  return data.slice(first, last + 1);
};
